import { Dispatch, SetStateAction, useRef, useState } from 'react';
import {
  LayoutCanvas,
  LayoutInlineForm,
  LayoutObject,
} from '../../../types/types';
import { snapSizeToGrid } from '../../../utils/layouts/snapSizeToGrid';
import { snapToGrid } from '../../../utils/layouts/snapToGrid';
import { clampObjectToCanvas } from '../../../utils/layouts/clampObjectToCanvas';
import { applyLayoutObjectDefaults } from '../../../utils/layouts/applyLayoutObjectDefaults';
import { getLayoutObjectInheritedStyle } from '../../../utils/layouts/getLayoutObjectInheritedStyle';
import { createLayoutObjectKey } from '../../../utils/layouts/createLayoutObjectKey';
import {
  LAYOUT_DEFAULT_KEY_SIZE_X,
  LAYOUT_DEFAULT_KEY_SIZE_Y,
  LAYOUT_PASTE_OFFSET,
} from '../../../constants';

interface UseLayoutObjectsParams {
  canvas: LayoutCanvas;
  isFixedMode: boolean;
  objectForm: LayoutInlineForm | undefined;
  // Last selected object — new objects inherit its colors, font and size.
  template: LayoutObject | null;
  // Editing a field discards its pending save-validation error; the errors
  // themselves stay owned by the provider.
  clearObjectFieldErrors: (index: number, patch: Partial<LayoutObject>) => void;
}

interface UseLayoutObjects {
  objects: LayoutObject[];
  setObjects: Dispatch<SetStateAction<LayoutObject[]>>;
  selectedIndex: number | null;
  setSelectedIndex: Dispatch<SetStateAction<number | null>>;
  // Exposed for the provider's context memo deps only — the copy mark itself is
  // read through isObjectCopied.
  copiedObject: { id: number } | { index: number } | null;
  selectObject: (index: number | null) => void;
  updateObject: (index: number, patch: Partial<LayoutObject>) => void;
  addObject: (x: number, y: number, width?: number, height?: number) => void;
  removeObject: (index: number) => void;
  moveObject: (fromIndex: number, toIndex: number) => void;
  copySelectedObject: () => void;
  pasteObject: (x?: number, y?: number) => void;
  isObjectCopied: (index: number) => boolean;
  clearCopiedObjectMark: () => void;
}

// Owns the object list of the layout editor and every mutation of it —
// selection, edits, add/paste/move/remove and the copy mark. The provider keeps
// the setters for the flows that write the list wholesale (initial load, reset,
// matrix generation, form defaults).
export const useLayoutObjects = ({
  canvas,
  isFixedMode,
  objectForm,
  template,
  clearObjectFieldErrors,
}: UseLayoutObjectsParams): UseLayoutObjects => {
  const [objects, setObjects] = useState<LayoutObject[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Source object of the last Ctrl+C — rendered with a "copied" frame on the
  // canvas; cleared by the next click into the canvas. Tracked by the server
  // id; objects created in the editor (no id until save) fall back to the index.
  const [copiedObject, setCopiedObject] = useState<
    { id: number } | { index: number } | null
  >(null);
  // Editor-internal clipboard (Ctrl+C/V on canvas objects) + cascade counter
  // shifting each repeated paste; both reset by the next copy.
  const clipboardRef = useRef<LayoutObject | null>(null);
  const pasteCountRef = useRef(0);

  // Selects the object at the given index; null clears the selection.
  const selectObject = (index: number | null): void => {
    setSelectedIndex(index);
  };

  // Merges the patch into the object at the given index (keys outside the patch stay untouched).
  const updateObject = (index: number, patch: Partial<LayoutObject>): void => {
    clearObjectFieldErrors(index, patch);
    setObjects((current) => {
      const next = current.map((object, i) => {
        if (i !== index) {
          return object;
        }

        // A cleared picture stays as backgroundImage: null — the server keeps
        // the stored bitmap when the key is omitted (unimplemented editor
        // features must survive a save), deleting requires the explicit null.
        return { ...object, ...patch };
      });

      return next;
    });
  };

  // Adds an empty slot (no `type`) at the given canvas position (snapped/clamped) and selects it.
  // Colors, font and size inherit from the last selected object (the template
  // context); the optional size (drag-drawn rect) wins over the inherited one.
  // The very first object (no template yet) gets the hardcoded default size.
  // Fixed mode appends a data-only slot to the end of the list instead (the
  // grid position is the array index; the PUT schema forbids geometry/styling).
  // No-op while canvas.lockItems is on (the layout arrangement is locked).
  const addObject = (
    x: number,
    y: number,
    width?: number,
    height?: number
  ): void => {
    if (canvas.lockItems) {
      return;
    }

    if (isFixedMode) {
      setObjects((current) => [
        ...current,
        {
          ...applyLayoutObjectDefaults({}, objectForm),
          feKey: createLayoutObjectKey(),
        },
      ]);
      setSelectedIndex(objects.length);
      return;
    }

    const inheritedStyle = getLayoutObjectInheritedStyle(template);

    const rawWidth = width ?? template?.width ?? LAYOUT_DEFAULT_KEY_SIZE_X;
    const rawHeight = height ?? template?.height ?? LAYOUT_DEFAULT_KEY_SIZE_Y;

    const newObject = clampObjectToCanvas(
      {
        x: canvas.snapGrid
          ? snapToGrid(x, canvas.gridX ?? 0, canvas.gapX ?? 0)
          : x,
        y: canvas.snapGrid
          ? snapToGrid(y, canvas.gridY ?? 0, canvas.gapY ?? 0)
          : y,
        // Drawn sizes snap to whole grid cells (spanning the gaps between them).
        width:
          canvas.snapGrid && width !== undefined
            ? snapSizeToGrid(rawWidth, canvas.gridX ?? 0, canvas.gapX ?? 0)
            : rawWidth,
        height:
          canvas.snapGrid && height !== undefined
            ? snapSizeToGrid(rawHeight, canvas.gridY ?? 0, canvas.gapY ?? 0)
            : rawHeight,
        ...inheritedStyle,
      },
      canvas.width,
      canvas.height
    );

    setObjects((current) => [
      ...current,
      {
        ...applyLayoutObjectDefaults(newObject, objectForm),
        feKey: createLayoutObjectKey(),
      },
    ]);
    setSelectedIndex(objects.length);
  };

  // Copies a deep snapshot of the selected object into the editor clipboard
  // (survives deselect and deletion of the source).
  const copySelectedObject = (): void => {
    if (selectedIndex === null) {
      return;
    }

    clipboardRef.current = JSON.parse(JSON.stringify(objects[selectedIndex]));
    pasteCountRef.current = 0;
    const copiedId = objects[selectedIndex].id;
    setCopiedObject(
      copiedId !== undefined ? { id: copiedId } : { index: selectedIndex }
    );
  };

  // The object at the given index is the source of the last copy.
  const isObjectCopied = (index: number): boolean => {
    if (!copiedObject) {
      return false;
    }

    if ('id' in copiedObject) {
      return objects[index]?.id === copiedObject.id;
    }

    return copiedObject.index === index;
  };

  // Clears the "copied" frame (the clipboard content itself stays usable).
  const clearCopiedObjectMark = (): void => {
    setCopiedObject(null);
  };

  // Appends a clone of the clipboard object and selects it. With a position
  // (the canvas cursor) the clone's top-left corner lands there; without one
  // (cursor outside the canvas) it cascades below the source like before.
  // Snapped/clamped like addObject; same no-op guards: lockItems and fixed
  // mode don't allow new objects.
  const pasteObject = (x?: number, y?: number): void => {
    const source = clipboardRef.current;

    if (!source || canvas.lockItems || isFixedMode) {
      return;
    }

    let rawX: number;
    let rawY: number;

    if (x !== undefined && y !== undefined) {
      rawX = x;
      rawY = y;
    } else {
      pasteCountRef.current += 1;
      const shift = LAYOUT_PASTE_OFFSET * pasteCountRef.current;
      rawX = (source.x ?? 0) + shift;
      rawY = (source.y ?? 0) + shift;
    }

    const clone = JSON.parse(JSON.stringify(source)) as LayoutObject;
    // The clone is a NEW item: with the source id it would update the stored
    // source in place on PUT (and inherit its "copied" frame). Fresh feKey —
    // the copied FE identity belongs to the source.
    delete clone.id;
    clone.feKey = createLayoutObjectKey();

    const newObject = clampObjectToCanvas(
      {
        ...clone,
        x: canvas.snapGrid
          ? snapToGrid(rawX, canvas.gridX ?? 0, canvas.gapX ?? 0)
          : rawX,
        y: canvas.snapGrid
          ? snapToGrid(rawY, canvas.gridY ?? 0, canvas.gapY ?? 0)
          : rawY,
      },
      canvas.width,
      canvas.height
    );

    setObjects((current) => [
      ...current,
      applyLayoutObjectDefaults(newObject, objectForm),
    ]);
    setSelectedIndex(objects.length);
  };

  // Fixed mode only: moves the object to another position in the ordered list
  // (the grid position is the array index). Selection follows the moved object.
  const moveObject = (fromIndex: number, toIndex: number): void => {
    if (!isFixedMode || canvas.lockItems || fromIndex === toIndex) {
      return;
    }

    if (fromIndex < 0 || fromIndex >= objects.length) {
      return;
    }

    const clampedTo = Math.min(Math.max(toIndex, 0), objects.length - 1);

    if (clampedTo === fromIndex) {
      return;
    }

    setObjects((current) => {
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(clampedTo, 0, moved);
      return next;
    });
    // The index-tracked "copied" frame follows the shifted positions.
    setCopiedObject((current) => {
      if (!current || 'id' in current) {
        return current;
      }

      if (current.index === fromIndex) {
        return { index: clampedTo };
      }

      if (fromIndex < current.index && current.index <= clampedTo) {
        return { index: current.index - 1 };
      }

      if (clampedTo <= current.index && current.index < fromIndex) {
        return { index: current.index + 1 };
      }

      return current;
    });
    setSelectedIndex(clampedTo);
  };

  // Removes the object at the given index. Works in fixed mode too — the
  // fixed grid reflows naturally (the position is the array index).
  // No-op while canvas.lockItems is on (the layout arrangement is locked).
  const removeObject = (index: number): void => {
    if (canvas.lockItems) {
      return;
    }

    setObjects((current) => current.filter((_, i) => i !== index));
    // Id-tracked frames follow their object automatically; the index fallback
    // (unsaved objects) shifts with the removal, removing the marked one drops it.
    setCopiedObject((current) => {
      if (!current || 'id' in current) {
        return current;
      }

      if (current.index === index) {
        return null;
      }

      return current.index > index ? { index: current.index - 1 } : current;
    });
    // The next object in order slides into the removed slot — select it; after
    // removing the last one fall back to the new last; empty canvas → nothing.
    const remainingCount = objects.length - 1;
    setSelectedIndex(
      remainingCount > 0 ? Math.min(index, remainingCount - 1) : null
    );
  };

  return {
    objects,
    setObjects,
    selectedIndex,
    setSelectedIndex,
    copiedObject,
    selectObject,
    updateObject,
    addObject,
    removeObject,
    moveObject,
    copySelectedObject,
    pasteObject,
    isObjectCopied,
    clearCopiedObjectMark,
  };
};
